import { observer } from 'mobx-react-lite';
import { style } from './ProductOptionsV3.style';
import { ScrollView, View } from 'react-native';
import TextLato from '../text/text-lato/TextLato';
import { ProductOptionsMobileProps } from './ProductOptionsV3.types';
import { useGridItemContentHeaderLogicV3 } from '../product-groups/grid-item-content/grid-item-content-header-v3/GridItemContentHeaderV3.logic';
import { Icon } from '../icon/Icon';
import { ICON_NAMES } from '../icon/icon.helper';
import { icon } from 'src/theme/colors';
import { LookupDropdownItem } from '../product-groups/grid-item-content/grid-item-content-header/LookupDropdownItem';
import { useEffect, useState } from 'react';
import { useVerticalScrollView } from 'src/hooks/useVerticalScrollView';
import { SECTION_NAMES } from 'src/constants/sectionNames.contants';
import { useProductLogic } from 'src/hooks/use-product/useProduct.logic';
import { PressableHovered } from 'src/editor-v3/components/pressable-hovered/PressableHovered';
import { FocusGroups, PRODUCT_FOCUS_ORDER_STEP } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { useFocusableGroups } from 'src/hooks/useFocusableGroup';
import { INSET_FOCUS_RING } from 'src/theme-v2/focus';

export const ProductOptionsV3 = observer(
  ({ product, containerStyle, onPressZoomInner, index = 0 }: ProductOptionsMobileProps) => {
    const { removeProduct } = useProductLogic();
    const {
      i18n,
      onPressEdit,
      isMultiphotoProductComplete,
      onPressQuality,
      onDuplicate,
      openLookup,
      getFormatOptions,
      applyLookupToProductGroup,
    } = useGridItemContentHeaderLogicV3(product);

    const productOptions = getFormatOptions();
    const [isDropdownLookupOpen, setIsDropdownLookupOpen] = useState(false);
    const { scrollVierRef, scrollToIndex, getScrollViewElementTestId } = useVerticalScrollView();
    const focusOrderBase = index * PRODUCT_FOCUS_ORDER_STEP;
    useFocusableGroups([FocusGroups.LOOKUP_CHOICE]);
    const onPressQualityInternal = () => {
      onPressQuality(true);
    };

    useEffect(() => {
      if (isDropdownLookupOpen) {
        const selected = productOptions?.findIndex(item => item.selected);
        scrollToIndex(selected);
      }
    }, [isDropdownLookupOpen]);

    return (
      <View style={[style.dropdownContainer, containerStyle]}>
        {!isDropdownLookupOpen ? (
          <>
            {product.userImagesWithQualityAlert.length > 0 && (
              <PressableHovered
                style={({ pressed, hovered }) => [style.action, (hovered || pressed) && style.buttonHover]}
                onPress={onPressQualityInternal}
                accessibilityLabel={i18n.t('actions:resolve-quality')}
                accessibilityRole="button"
                focusGroup={FocusGroups.LOOKUP_CHOICE}
                focusOrder={focusOrderBase + 0}
                focusStyle={INSET_FOCUS_RING}
              >
                <View style={style.actionContent}>
                  <TextLato style={style.actionText}>{i18n.t('actions:resolve-quality')}</TextLato>
                  <View style={{ width: 14, height: 14 }} accessible={false}>
                    <Icon name={ICON_NAMES.Alert} color={icon.iconColorAlert} />
                  </View>
                </View>
              </PressableHovered>
            )}
            {!!productOptions?.length && (
              <PressableHovered
                style={({ pressed, hovered }) => [style.action, (hovered || pressed) && style.buttonHover]}
                onPress={() => setIsDropdownLookupOpen(true)}
                accessibilityLabel={i18n.t('actions:change-format')}
                accessibilityRole="button"
                focusGroup={FocusGroups.LOOKUP_CHOICE}
                focusOrder={focusOrderBase + 1}
                focusStyle={INSET_FOCUS_RING}
              >
                <TextLato style={style.actionText}>{i18n.t('actions:change-format')}</TextLato>
              </PressableHovered>
            )}
            {!product.isSelected && (
              <PressableHovered
                style={({ pressed, hovered }) => [style.action, (hovered || pressed) && style.buttonHover]}
                onPress={openLookup}
                accessibilityLabel={i18n.t('actions:print-options')}
                accessibilityRole="button"
                focusGroup={FocusGroups.LOOKUP_CHOICE}
                focusOrder={focusOrderBase + 2}
                focusStyle={INSET_FOCUS_RING}
              >
                <TextLato style={style.actionText}>{i18n.t('actions:print-options')}</TextLato>
              </PressableHovered>
            )}
            <PressableHovered
              style={({ pressed, hovered }) => [style.action, (hovered || pressed) && style.buttonHover]}
              onPress={() => onPressEdit(isMultiphotoProductComplete)}
              accessibilityLabel={i18n.t('actions:edit')}
              accessibilityRole="button"
              focusGroup={FocusGroups.LOOKUP_CHOICE}
              focusOrder={focusOrderBase + 3}
              focusStyle={INSET_FOCUS_RING}
            >
              <TextLato style={style.actionText}>{i18n.t('actions:edit')}</TextLato>
            </PressableHovered>
            <PressableHovered
              style={({ pressed, hovered }) => [style.action, (hovered || pressed) && style.buttonHover]}
              onPress={onDuplicate}
              accessibilityLabel={i18n.t('actions:duplicate')}
              accessibilityRole="button"
              focusGroup={FocusGroups.LOOKUP_CHOICE}
              focusOrder={focusOrderBase + 4}
              focusStyle={INSET_FOCUS_RING}
            >
              <TextLato style={style.actionText}>{i18n.t('actions:duplicate')}</TextLato>
            </PressableHovered>
            <PressableHovered
              style={({ pressed, hovered }) => [style.action, (hovered || pressed) && style.buttonHover]}
              onPress={onPressZoomInner}
              accessibilityLabel={i18n.t('actions:zoom')}
              accessibilityRole="button"
              focusGroup={FocusGroups.LOOKUP_CHOICE}
              focusOrder={focusOrderBase + 5}
              focusStyle={INSET_FOCUS_RING}
            >
              <TextLato style={style.actionText}>{i18n.t('actions:zoom')}</TextLato>
            </PressableHovered>
            <PressableHovered
              style={({ pressed, hovered }) => [style.actionLast, (hovered || pressed) && style.buttonHover]}
              onPress={() => removeProduct({ product, sectionName: SECTION_NAMES.OPTIONS_MENU })}
              accessibilityLabel={i18n.t('actions:delete')}
              accessibilityRole="button"
              focusGroup={FocusGroups.LOOKUP_CHOICE}
              focusOrder={focusOrderBase + 6}
              focusStyle={INSET_FOCUS_RING}
            >
              <TextLato style={style.actionTextDelete}>{i18n.t('actions:delete')}</TextLato>
            </PressableHovered>
          </>
        ) : (
          <>
            <PressableHovered
              style={style.optionsTitle}
              onPress={() => setIsDropdownLookupOpen(false)}
              accessibilityLabel={i18n.t('actions:choose-format')}
              accessibilityRole="button"
              focusGroup={FocusGroups.LOOKUP_CHOICE}
              focusOrder={focusOrderBase + 0}
              focusStyle={INSET_FOCUS_RING}
            >
              <View style={style.optionsTitleContent}>
                <Icon
                  name={ICON_NAMES.ArrowLeft2}
                  color={icon.iconInteractive}
                  width={14}
                  height={14}
                  accessible={false}
                />
                <TextLato style={style.actionText}>{i18n.t('actions:choose-format')}</TextLato>
              </View>
            </PressableHovered>
            <ScrollView style={style.scrollViewVertical} ref={scrollVierRef}>
              {productOptions?.map((item, itemIndex) => (
                <View testID={getScrollViewElementTestId(itemIndex)} key={item.titleKey}>
                  <LookupDropdownItem
                    key={item.productOptionChoiceCode}
                    item={item}
                    focusOrder={focusOrderBase + 1 + itemIndex}
                    onPress={item => {
                      setIsDropdownLookupOpen(false);
                      applyLookupToProductGroup(item);
                    }}
                  />
                </View>
              ))}
            </ScrollView>
          </>
        )}
      </View>
    );
  },
);
